import { bisector } from "d3-array";
import { axisBottom, axisLeft } from "d3-axis";
import {
  ScaleBand,
  scaleBand,
  ScaleLinear,
  scaleLinear,
  ScaleTime,
  scaleTime,
} from "d3-scale";
import { pointer, select, Selection } from "d3-selection";
import { Line, line } from "d3-shape";
import { zoom, zoomTransform } from "d3-zoom";
import { add, sub } from "date-fns";

import { sleepStageOrder } from "./constants";
import {
  Measurement,
  MeasurementLine,
  SleepStageData,
  SleepStagesChartData,
  TagData,
} from "./types";

import { clamp } from "../shared/clamp";
import { renderSideBars } from "../shared/render/sideBars";
import { Size } from "../shared/types";

const padding = {
  top: 40,
  right: 0,
  bottom: 35,
  left: 70,
};

const LINES_SECTION_HEIGHT = 150;
const SECTION_GAP = 10;

function renderBars(
  group: Selection<SVGGElement, unknown, null, undefined>,
  sleepStages: SleepStageData[],
  stages: SleepStagesChartData["stages"],
  x: ScaleTime<number, number>,
  y: ScaleBand<string>,
  barsBottom: number,
) {
  const barWidth = y.bandwidth() / 2;

  const bars = group
    .selectAll<SVGRectElement, unknown>("rect")
    .data(sleepStages);

  bars
    .enter()
    .append("rect")
    .merge(bars)
    .attr(
      "fill",
      ({ stage }) => stages.find((s) => s.sleepStage === stage)?.color ?? "",
    )
    .attr("height", ({ stage }) => {
      const val = y(stage);
      if (val == null) return 0;
      return Math.max(barsBottom - val - barWidth, 0);
    })
    .attr("width", ({ startDatetime, endDatetime }) =>
      Math.max(x(endDatetime) - x(startDatetime), 0),
    )
    .attr("y", ({ stage }) => {
      const val = y(stage);
      if (val == null) return barsBottom;
      return val + barWidth;
    })
    .attr("x", ({ startDatetime }) => x(startDatetime));

  bars.exit().remove();
}

function renderTags(
  group: Selection<SVGGElement, unknown, null, undefined>,
  tags: TagData[],
  x: ScaleTime<number, number>,
  size: Size,
) {
  const existing = group.selectAll<SVGGElement, unknown>("g").data(tags);

  const entering = existing.enter().append("g");
  entering.append("line");
  entering.append("rect");
  entering.append("text");
  entering.append("circle");

  const all = entering.merge(existing);

  all.attr("transform", (d) => `translate(${x(d.datetime)} , 0)`);

  all
    .select("line")
    .attr("x1", 0)
    .attr("x2", 0)
    .attr("y1", size.height - padding.bottom)
    .attr("y2", (d) => padding.top - d.offsetIndex * 24 - 12);

  all
    .select("rect")
    .attr("rx", 2)
    .attr("x", -64 / 2)
    .attr("y", (d) => padding.top - d.offsetIndex * 24 - 12)
    .attr("width", 64)
    .attr("height", 16);

  all
    .select("text")
    .attr("x", 0)
    .attr("y", (d) => padding.top - d.offsetIndex * 24)
    .text((d) => d.label);

  all
    .select("circle")
    .attr("r", 4)
    .attr("cx", 0)
    .attr("cy", size.height - padding.bottom);

  existing.exit().remove();
}

function renderLines(
  group: Selection<SVGGElement, unknown, null, undefined>,
  lines: MeasurementLine[],
  lineGenerator: Line<Measurement>,
  lineGeneratorNonZero: Line<Measurement>,
) {
  const paths = group.selectAll<SVGPathElement, unknown>("path").data(lines);

  paths
    .enter()
    .append("path")
    .merge(paths)
    .attr("data-testid", (d) => `${d.id}-line`)
    .attr("stroke", (d) => d.color)
    .attr("stroke-width", (d) => d.width)
    .attr("stroke-dasharray", (d) => (d.style === "dashed" ? 4 : 0))
    .attr("fill", "none")
    .attr("d", (d) =>
      d.gaps ? lineGeneratorNonZero(d.data) : lineGenerator(d.data),
    );

  paths.exit().remove();
}

type TooltipScales = {
  x: ScaleTime<number, number>;
  y: ScaleBand<string>;
  yLines: ScaleLinear<number, number>;
  barsBottom: number;
  hasLines: boolean;
};

function setupTooltip(
  svg: Selection<SVGSVGElement, unknown, null, undefined>,
  ref: SVGSVGElement,
  size: Size,
  data: SleepStagesChartData,
  { x, y, yLines, barsBottom, hasLines }: TooltipScales,
) {
  const barWidth = y.bandwidth() / 2;

  const host = ref.parentElement ?? document.body;

  const tooltip = select(host)
    .select<HTMLDivElement>("div.tooltip")
    .style("display", "none");

  const focusGroup = svg
    .selectAll<SVGGElement, unknown>("g.focus")
    .data([null])
    .join("g")
    .attr("class", "focus")
    .style("display", "none");

  focusGroup
    .selectAll<SVGLineElement, unknown>("line.focus-line")
    .data([null])
    .join("line")
    .attr("class", "focus-line")
    .attr("y1", padding.top)
    .attr("y2", size.height - padding.bottom)
    .attr("stroke", "#999")
    .attr("stroke-width", 1)
    .attr("stroke-dasharray", "4,4");

  focusGroup
    .selectAll<SVGCircleElement, unknown>("circle.stage-dot")
    .data([null])
    .join("circle")
    .attr("class", "stage-dot")
    .attr("r", 5)
    .attr("stroke", "#fff")
    .attr("stroke-width", 1.5);

  if (hasLines && data.lines) {
    focusGroup
      .selectAll<SVGCircleElement, MeasurementLine>("circle.focus-dot")
      .data(data.lines, (d: any) => d.id ?? d.label ?? d.color)
      .join("circle")
      .attr("class", "focus-dot")
      .attr("r", 3.5)
      .attr("fill", (d) => d.color)
      .attr("stroke", "#fff")
      .attr("stroke-width", 1.5);
  } else {
    focusGroup.selectAll("circle.focus-dot").remove();
  }

  const snapDates: Date[] = data.snapData.map((m) => new Date(m.datetime));
  const bisectSnap = bisector<Date, Date>((d) => d).left;

  const snapToNearestDate = (d: Date): Date => {
    if (snapDates.length === 0) return d;
    const i = bisectSnap(snapDates, d, 1);
    const d0 = snapDates[i - 1];
    const d1 = snapDates[i];
    if (!d0) return d1 ?? d;
    if (!d1) return d0;
    return d.getTime() - d0.getTime() > d1.getTime() - d.getTime() ? d1 : d0;
  };

  const overlay = svg
    .selectAll<SVGRectElement, unknown>("rect.hover-overlay")
    .data([null])
    .join("rect")
    .attr("class", "hover-overlay")
    .attr("data-testid", "hover-overlay")
    .attr("x", padding.left)
    .attr("y", padding.top)
    .attr("width", Math.max(size.width - padding.left - padding.right, 0))
    .attr("height", Math.max(size.height - padding.top - padding.bottom, 0))
    .attr("fill", "transparent");

  const onMouseMove = (event: MouseEvent) => {
    const [mouseX] = pointer(event, ref);
    const hoveredDate = x.invert(mouseX);
    const snappedDate = snapToNearestDate(hoveredDate);

    let snapIdx =
      snapDates.length > 0 ? bisectSnap(snapDates, snappedDate) : -1;
    if (snapIdx >= 0) snapIdx = clamp(snapIdx, 0, snapDates.length - 1);

    const displayDate = snapIdx >= 0 ? snapDates[snapIdx] : snappedDate;
    const snappedX = x(displayDate);

    focusGroup
      .select<SVGLineElement>("line.focus-line")
      .attr("x1", snappedX)
      .attr("x2", snappedX);

    if (hasLines && data.lines && snapIdx >= 0) {
      focusGroup
        .selectAll<SVGCircleElement, MeasurementLine>("circle.focus-dot")
        .attr("cx", snappedX)
        .attr("cy", (ln) => {
          const m = ln.data[snapIdx];
          if (!m || !Number.isFinite(m.normalizedValue)) return -9999;
          return yLines(m.normalizedValue);
        });
    }

    const snapStage =
      snapIdx >= 0 ? String(data.snapData[snapIdx]?.value) : null;
    const currentStage = snapStage
      ? data.stages.find((s) => s.sleepStage === snapStage)
      : null;

    focusGroup
      .select<SVGCircleElement>("circle.stage-dot")
      .attr("cx", snappedX)
      .attr("cy", snapStage ? (y(snapStage) ?? barsBottom) + barWidth : -9999)
      .attr("fill", currentStage?.color ?? "transparent");

    const stagePrefix = data.stageTooltipLabel
      ? `${data.stageTooltipLabel}: `
      : "";
    const stageHtml = currentStage
      ? `<div style="color:${currentStage.color};margin-bottom:2px">${stagePrefix}${currentStage.label}</div>`
      : "";

    const linesHtml =
      hasLines && data.lines && snapIdx >= 0
        ? data.lines
            .map((ln) => {
              const m = ln.data[snapIdx];
              if (!m || !Number.isFinite(m.value)) {
                return `<div style="opacity:0.7;color:${ln.color}">${ln.label}: —</div>`;
              }
              return `<div style="color:${ln.color}">${ln.label}: ${ln.formatTooltip(m.value)}</div>`;
            })
            .join("")
        : "";

    const dateStr = data.formatTooltipDate
      ? data.formatTooltipDate(displayDate)
      : displayDate.toLocaleString();

    tooltip.html(
      `<div style="margin-bottom:4px"><strong>${dateStr}</strong></div>${stageHtml}${linesHtml}`,
    );

    const hostRect = host.getBoundingClientRect();
    const tooltipNode = tooltip.node();
    const tw = tooltipNode?.offsetWidth ?? 120;
    const th = tooltipNode?.offsetHeight ?? 40;

    tooltip
      .style(
        "left",
        `${clamp(event.clientX - hostRect.left + 12, 0, hostRect.width - tw)}px`,
      )
      .style(
        "top",
        `${clamp(event.clientY - hostRect.top + 12, 0, hostRect.height - th)}px`,
      );
  };

  overlay
    .on("mouseenter", () => {
      focusGroup.style("display", null);
      tooltip.style("display", "block");
    })
    .on("mouseleave", () => {
      focusGroup.style("display", "none");
      tooltip.style("display", "none");
    })
    .on("mousemove", onMouseMove);
}

export const sleepStagesUpdate = (
  ref: SVGSVGElement,
  size: Size,
  data: SleepStagesChartData,
) => {
  const hasLines = (data.lines?.length ?? 0) > 0;

  const barsBottom = hasLines
    ? size.height - padding.bottom - LINES_SECTION_HEIGHT - SECTION_GAP
    : size.height - padding.bottom;

  // Only meaningful when hasLines; yLines scale is unused otherwise
  const linesTop = barsBottom + SECTION_GAP;

  const tickCount = size.width / 100;

  const x = scaleTime()
    .domain([
      sub(data.sleepStages.at(0)?.startDatetime ?? new Date(), { minutes: 30 }),
      add(data.sleepStages.at(-1)?.endDatetime ?? new Date(), { minutes: 30 }),
    ])
    .range([padding.left, size.width - padding.right]);

  const y = scaleBand()
    .padding(0)
    .domain(sleepStageOrder)
    .range([barsBottom, padding.top]);

  const yLines = scaleLinear()
    .domain([0, 1])
    .range([size.height - padding.bottom, linesTop]);

  const lineGenerator = line<Measurement>()
    .x((d) => x(new Date(d.datetime)))
    .y((d) => yLines(d.normalizedValue))
    .defined((d) => Number.isFinite(d.normalizedValue));

  const lineGeneratorNonZero = line<Measurement>()
    .x((d) => x(new Date(d.datetime)))
    .y((d) => yLines(d.normalizedValue))
    .defined((d) => d.value > 0);

  const xAxis = axisBottom(x)
    .ticks(tickCount)
    .tickFormat((val) =>
      val instanceof Date
        ? data.formatXAxisTicks(val)
        : data.formatXAxisTicks(val.valueOf()),
    );

  const zoomHandler = (
    svg: Selection<SVGSVGElement, unknown, null, undefined>,
  ) => {
    const extent: [[number, number], [number, number]] = [
      [padding.left, padding.top],
      [size.width - padding.right, size.height - padding.top],
    ];

    // Always prevent the browser from zooming the page on ctrl+wheel/pinch,
    // even when D3 returns early (e.g. already at scale limit).
    svg.on(
      "wheel.preventBrowserZoom",
      (event: WheelEvent) => {
        if (event.ctrlKey) event.preventDefault();
      },
      { passive: false },
    );

    svg.call(
      zoom<SVGSVGElement, unknown>()
        .scaleExtent([1, 50])
        .translateExtent(extent)
        .extent(extent)
        .wheelDelta((event) => {
          // deltaMode: 0 = pixels, 1 = lines, 2 = pages
          const modeScales: Record<number, number> = { 0: 0.002, 1: 0.05 };
          const modeScale = modeScales[event.deltaMode] ?? 1;
          return -event.deltaY * modeScale;
        })
        .filter((event) => {
          if (event.type === "wheel") return event.ctrlKey;
          // Only capture multi-touch (pinch); let single-finger scroll the page
          if (event.type === "touchstart" || event.type === "touchmove")
            return event.touches.length >= 2;
          return !event.button;
        })
        .on("zoom", (event) => {
          x.range(
            [padding.left, size.width - padding.right].map((d) =>
              event.transform.applyX(d),
            ),
          );

          svg
            .select<SVGGElement>("g.bars")
            .selectAll<SVGRectElement, SleepStageData>("rect")
            .attr("x", (d) => x(d.startDatetime))
            .attr("width", (d) => x(d.endDatetime) - x(d.startDatetime));

          svg
            .select<SVGGElement>("g.tags")
            .selectAll<SVGRectElement, TagData>("g")
            .attr("transform", (d) => `translate(${x(d.datetime)} , 0)`);

          if (hasLines) {
            svg
              .select<SVGGElement>("g.lines")
              .selectAll<SVGPathElement, MeasurementLine>("path")
              .attr("d", (d) =>
                d.gaps ? lineGeneratorNonZero(d.data) : lineGenerator(d.data),
              );
          }

          svg
            .select<SVGGElement>("g.xAxis")
            .call(xAxis.ticks(event.transform.k * tickCount));
        }),
    );
  };

  const svg = select(ref).call(zoomHandler);

  // Re-apply the persisted zoom transform to the new x-scale so the view is
  // preserved when the component re-renders (e.g. toggling a line series).
  const currentTransform = zoomTransform(ref);
  x.range(
    [padding.left, size.width - padding.right].map((d) =>
      currentTransform.applyX(d),
    ),
  );

  renderSideBars(svg, size, { ...padding, top: 60, bottom: 0 });

  const xAxisGroup = svg.select<SVGGElement>("g.xAxis");
  const yAxisGroup = svg.select<SVGGElement>("g.yAxis");

  xAxisGroup.attr("transform", `translate(0 ${size.height - padding.bottom})`);
  yAxisGroup.attr("transform", `translate(${padding.left} 0)`);

  const yAxis = axisLeft(y)
    .tickSize(0)
    .tickPadding(12)
    .tickFormat(
      (v) => data.stages.find((s) => s.sleepStage === v)?.label ?? "",
    );

  xAxisGroup.call(xAxis);
  yAxisGroup.call(yAxis);

  renderBars(
    svg.select<SVGGElement>("g.bars"),
    data.sleepStages,
    data.stages,
    x,
    y,
    barsBottom,
  );

  renderTags(svg.select<SVGGElement>("g.tags"), data.tags, x, size);

  renderLines(
    svg.select<SVGGElement>("g.lines"),
    data.lines ?? [],
    lineGenerator,
    lineGeneratorNonZero,
  );

  setupTooltip(svg, ref, size, data, { x, y, yLines, barsBottom, hasLines });
};
